import { Dispatch, SetStateAction } from "react";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { useMemoizedFn } from "ahooks";
import { SwapModal } from "../swap/SwapModal";
import { DrawerH5 } from "../DrawerH5";
import { AIInsightThemes } from "./AIInsightThemes";
import Translations from "@translate/components/AIInsight/AIInsightThemesModal";
import { I18nUtil } from "@utils/i18n";
import { ThemeWithMarketData } from "@/src/componentsv1/themes/type";
import { Locale } from "@/src/utils";

const I18n = I18nUtil.createScoped(Translations);

interface AIInsightThemesModalProps {
  visible: boolean;
  onClose: () => void;
  themesList: ThemeWithMarketData[];
  selectedTheme: ThemeWithMarketData;
  setSelectedTheme: Dispatch<SetStateAction<ThemeWithMarketData>>;
  deviceType: "mobile" | "desktop";
  category?: string;
  locale?: Locale;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  fetchNextPage: () => void;
}

export const AIInsightThemesModal = (props: AIInsightThemesModalProps) => {
  const {
    visible,
    onClose,
    selectedTheme,
    setSelectedTheme,
    deviceType,
    category = "crypto",
    locale = "en",
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    themesList,
  } = props;
  const modalSelectedTheme = useMemoizedFn((theme: ThemeWithMarketData) => {
    setSelectedTheme(theme);
    onClose();
  });

  // 移动端使用 DrawerH5，避免键盘弹起时被顶起
  if (deviceType !== "desktop") {
    return (
      <DrawerH5
        open={visible}
        onClose={onClose}
        containerStyle={{
          maxHeight: "calc(100vh - 120px)",
        }}
        contentStyle={{
          maxHeight: "calc(100vh - 60px)",
        }}
      >
        <AIInsightThemes
          data-openreplay-label="AIInsightThemesModal · AIInsightThemes"
          className={"mt-4 px-4"}
          category={category}
          locale={locale}
          selectedTheme={selectedTheme}
          setSelectedTheme={modalSelectedTheme}
          deviceType={deviceType}
          dynamicThemesList={themesList}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          closeComponent={
            <button
              data-openreplay-label="AIInsightThemesModal · button"
              className="h-6 w-6 flex items-center justify-center"
              onClick={onClose}
            >
              <Icon
                name="close"
                size={16}
                color={ospThemes.colors.content_tertiary}
              />
            </button>
          }
        />
      </DrawerH5>
    );
  }

  // 桌面端继续使用 SwapModal
  return (
    <SwapModal
      visible={visible}
      onClose={onClose}
      mask={true}
      placement={"bottom"}
      className="!rounded-tl-[16px] !rounded-tr-[16px] !rounded-bl-[0px] !rounded-br-[0px] h-[700px] p-4"
    >
      <div className={`flex flex-row gap-4 justify-between`}>
        <div className="headline_regular_s text-content_primary">
          {I18n.t("Select a Theme")}
        </div>
        <button
          aria-label="AIInsight Themes Modal"
          data-openreplay-label="AIInsight Themes Modal Button"
          id="btn-aiinsight-themes-modal"
          className="h-6 w-6 flex items-center justify-center"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={16}
            color={ospThemes.colors.content_tertiary}
          />
        </button>
      </div>
      <AIInsightThemes
        className={"mt-4"}
        category={category}
        locale={locale}
        selectedTheme={selectedTheme}
        setSelectedTheme={modalSelectedTheme}
        deviceType={deviceType}
        dynamicThemesList={themesList}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
      />
    </SwapModal>
  );
};
